import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import WebApp from '@twa-dev/sdk';

import { accessTokenStorage, registerUnauthorizedHandler } from '../../api/client';
import type { UserRead } from '../../api/types';
import { useTelegramAuth } from '../../features/auth/hooks/useTelegramAuth';
import { readAcquisitionSource } from '../../features/auth/lib/acquisitionSource';
import {
  applyBackendLocaleIfNoManualChoice,
  enforceActiveLocale,
  type SupportedLocale,
} from '../../i18n';
import { AuthContext, type AuthStatus } from './useAuth';

interface AuthProviderProps {
  children: ReactNode;
}

// PARA-105, AC-1/AC-E1, §8.3 — инициализация Telegram WebApp SDK, аутентификация
// при монтировании, единая обработка потери авторизации (401 в любой момент сессии).
export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const queryClient = useQueryClient();
  const { mutate } = useTelegramAuth();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<UserRead | null>(null);

  const login = useCallback(() => {
    setStatus('loading');
    WebApp.ready();
    mutate(
      // PARA-127, AC-7: метка источника из URL Mini App (кнопка бота).
      { initData: WebApp.initData, source: readAcquisitionSource() },
      {
        onSuccess: (response) => {
          accessTokenStorage.set(response.access_token);
          applyBackendLocaleIfNoManualChoice(response.user.locale);
          // PARA-138: эффективный список = пересечение user.available_locales и
          // глобального supported_locales; null = использовать глобальный список.
          const effectiveLocales = (
            response.user.available_locales ?? response.supported_locales
          ).filter((locale) => response.supported_locales.includes(locale));
          enforceActiveLocale(effectiveLocales as SupportedLocale[]);
          setUser(response.user);
          setStatus('authenticated');
        },
        onError: () => {
          // 401 уже обработан registerUnauthorizedHandler ниже; здесь покрываются
          // и прочие ошибки (сеть, 5xx).
          setUser(null);
          setStatus('error');
        },
      },
    );
  }, [mutate]);

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      accessTokenStorage.clear();
      queryClient.clear();
      setUser(null);
      setStatus('error');
    });
  }, [queryClient]);

  useEffect(() => {
    login();
  }, [login]);

  // PARA-132 (AC-7) — точечная замена профиля внутри сессии (согласие на голос).
  // Статус авторизации при этом не меняется: это не новый вход.
  const applyUser = useCallback((next: UserRead) => setUser(next), []);

  return (
    <AuthContext.Provider value={{ status, user, retry: login, applyUser }}>
      {children}
    </AuthContext.Provider>
  );
}
